import React, { useCallback, useState } from "react";
import { message } from "antd";

import { Patient } from "../../types/healthcare/PatientTypes";
import { Test } from "../../types/healthcare/TestTypes";

import { fetchTests } from "../../api/healthcare/Testing/TestApi";
import { fetchPatient } from "../../api/healthcare/PatientApi";

import CreateTestModal from "../../pages/healthcare/results/CreateTestOrderModal";
import type { UserUuid } from "../../types/identity";

/**
 * Opens the test-order modal (`<CreateTestModal>`) for a patient, anywhere in the app.
 * Generalised from the test-order branch that used to live inside `useReferenceModal`:
 * it is keyed by `patientId` and an optional `testId`.
 *
 *  - With a `testId`: `fetchTests(patientId)` → find the order by id → open it for
 *    viewing/editing. A not-found order warns and no-ops.
 *  - Without a `testId`: opens a fresh blank order for that patient (the modal treats
 *    an order with no `id` as a new order).
 *
 * The modal needs the patient object (subtitle + new-order `patient_id` derivation), so
 * the hook fetches it by id the same way `PatientsPage`/`useOpenPatientModal` does
 * (`fetchPatient(String(id))` → `{ data }`). Mirrors `useReferenceModal`'s local-state
 * pattern: `message.loading(...)` spinner, graceful warn/error on a missing/failed
 * fetch, and a `modals` fragment rendered once by the caller.
 */
export function useOpenTestOrderModal(): {
	openTestOrder: (patientId: UserUuid, testId?: number) => void;
	modals: React.ReactNode;
} {
	const [testOrder, setTestOrder] = useState<Partial<Test> | null>(null);
	const [testPatient, setTestPatient] = useState<Patient | undefined>(undefined);
	const [testModalOpen, setTestModalOpen] = useState(false);

	// Best-effort single-patient fetch — reused for both branches. Never throws: a
	// failed lookup just leaves the modal without an enriched patient object.
	const loadPatient = useCallback(async (patientId: UserUuid): Promise<Patient | undefined> => {
		try {
			const { data } = await fetchPatient(String(patientId));
			return data ?? undefined;
		} catch {
			return undefined;
		}
	}, []);

	const openTestOrder = useCallback((patientId: UserUuid, testId?: number) => {
		const run = async () => {
			if (!patientId) {
				message.warning("This item is not linked to a patient.");
				return;
			}
			const hide = message.loading("Loading test order…", 0);
			try {
				if (testId !== undefined && testId !== null && !Number.isNaN(Number(testId))) {
					const tests = await fetchTests(patientId);
					const found = tests.find((t) => t.id === Number(testId));
					if (!found) {
						message.warning("The referenced test order could not be found.");
						return;
					}
					const patient = await loadPatient(patientId);
					setTestPatient(patient ?? found.patient);
					setTestOrder({ ...found, patient: patient ?? found.patient });
				} else {
					// No testId → open a new/blank order seeded only with the patient.
					const patient = await loadPatient(patientId);
					setTestPatient(patient);
					setTestOrder({ patient_id: patientId, patient });
				}
				setTestModalOpen(true);
			} catch {
				message.error("Failed to load the test order.");
			} finally {
				hide();
			}
		};
		void run();
	}, [loadPatient]);

	const modals = (
		<>
			{testModalOpen && testOrder && (
				<CreateTestModal
					isOpen={testModalOpen}
					onClose={() => setTestModalOpen(false)}
					currentOrder={testOrder}
					setCurrentOrder={setTestOrder}
					patient={testPatient}
				/>
			)}
		</>
	);

	return { openTestOrder, modals };
}

export default useOpenTestOrderModal;
